import Reflux from "reflux";

import _ from "lodash";
import { getUserId } from "../hooks/query/useAccount";
import AppConstants from "../constants/AppConstants";
import UIActions from "../actions/UIActions";
import DomainActions from "../actions/DomainActions";
import UserService from "../service/UserService";
import APIError from "../service/APIError";
import ErrorUtils from "../utils/ErrorUtils";
import ErrorCodes from "../constants/ErrorCodes";
import { USER_DOMAINS } from "../constants/QueryKeys";
import { subscribeToQuery } from "../utils/queryCacheBridge";
import {
  addDomainToCache,
  fetchUserDomains,
  getDomainBySiteId,
  getUserDomainsData,
  updateDomainInCache,
} from "../hooks/query/useUserDomains";

var VIEW_SELECT = "select";
var VIEW_MANAGE = "manage";

var model = {
  selectedDomain: null,
  view: null,
  views: {
    select: {
      domains: [],
      domainsLoaded: false,
      domainsLoading: false,
      domainsError: false,
      activeTab: "enabled",
      addSubdomainEnabled: false,
      subdomainSaving: false,
      subdomainAddError: false,
    },
    manage: {
      saving: false,
      error: false,
      updated: false,
      primaryDomain: null,
    },
  },
};

var DomainManagementStore = Reflux.createStore({
  storeName: 'DomainManagementStore',
  listenables: [DomainActions],

  init() {
    this.state = {};
    this._userDomainsUnsubscribe = null;
  },

  onDomainManageOpen(data) {
    UIActions.modalOpen(AppConstants.MODAL_DOMAIN_MANAGER, {
      onHide: DomainActions.domainManageClose,
    });
  },

  onDomainManageClose() {
    this._userDomainsUnsubscribe?.();
    this._userDomainsUnsubscribe = null;

    UIActions.modalClose(AppConstants.MODAL_DOMAIN_MANAGER);
  },

  onUpdateView(view, data) {
    var viewState = this.state.views[view];
    if (viewState) {
      _.merge(viewState, data);
      this.state.views[view] = viewState;
      this.trigger(this.state);
    }
  },

  onSelectDomain(data) {
    if (_.isEmpty(data.domain)) {
      this.state.view = VIEW_SELECT;
      this.state.selectedDomain = null;
    } else {
      var domain = data.domain;

      this._resetManageView();

      this.state.selectedDomain = domain;
      if (_.isNumber(domain.parent_site_id)) {
        this.state.views.manage.primaryDomain = getDomainBySiteId(
          getUserDomainsData()?.domains,
          domain.parent_site_id
        );
      }

      this.state.view = VIEW_MANAGE;
    }

    this.trigger(this.state);
  },

  _resetManageView() {
    this.state.views.manage.saving = false;
    this.state.views.manage.error = false;
    this.state.views.manage.primaryDomain = null;
    this.state.views.manage.updated = false;
  },

  onAddSubdomain(data) {
    if (_.has(data, "web_name") && !_.isEmpty(data.web_name) && _.has(data, "parent_site_id")) {
      this.state.views.select.subdomainSaving = true;
      this.state.views.select.subdomainAddError = false;
      this.trigger(this.state);

      var subdomainInfo = _.pick(data, ["web_name", "parent_site_id"]);

      var parentDomainId = data.parent_site_id;

      UserService.addSubdomain(getUserId(), parentDomainId, subdomainInfo)
        .then(this._onAddSubdomainComplete.bind(this))
        .catch(this._onAddSubdomainFailed.bind(this));
    }
  },

  _onAddSubdomainComplete(data) {
    var newSubdomain = data.domain;

    this.state.views.select.subdomainSaving = false;
    this.state.views.select.addSubdomainEnabled = false;

    addDomainToCache(newSubdomain);
    this.state.domains = getUserDomainsData()?.domains ?? [];

    this.trigger(this.state);
  },

  _onAddSubdomainFailed(err) {
    if (APIError.handle(err)) {
      return;
    }

    var error = ErrorUtils.getFormattedError(err);

    if (ErrorCodes.DOMAIN_IN_USE == error.code) {
      error.message = "That name is already in use";
    }

    this.state.views.select.subdomainSaving = false;
    this.state.views.select.subdomainAddError = error;

    this.trigger(this.state);
  },

  onEnableSubdomain(subdomain) {
    var update = { disabled: 0 };

    this._updateSubdomain(subdomain, update);
  },

  onDisableSubdomain(subdomain) {
    var update = { disabled: 1 };

    this._updateSubdomain(subdomain, update);
  },

  onRenameSubdomain(subdomain, data) {
    this._updateSubdomain(subdomain, data);
  },

  _updateSubdomain(subdomain, data) {
    if (subdomain && _.isNumber(subdomain.parent_site_id)) {
      var updatedSubdomain = _.clone(subdomain);
      _.merge(updatedSubdomain, data);

      if (!_.isEqual(subdomain, updatedSubdomain)) {
        this.state.selectedDomain = updatedSubdomain; //optimistic update

        this.state.views.manage.saving = false;
        this.state.views.manage.error = null;
        this.state.views.manage.updated = false;

        this.trigger(this.state);

        UserService.updateSubdomain(getUserId(), updatedSubdomain)
          .then(this._onUpdateSubdomainComplete.bind(this))
          .catch(this._onUpdateSubdomainFailed.bind(this));
      }
    }
  },

  _onUpdateSubdomainComplete(data) {
    var subdomain = data.domain;

    updateDomainInCache(subdomain.site_id, () => subdomain);

    this.state.selectedDomain = subdomain;
    this.state.domains = getUserDomainsData()?.domains ?? [];
    this.state.views.manage.saving = false;
    this.state.views.manage.updated = true;

    this.trigger(this.state);
  },

  _onUpdateSubdomainFailed(err) {
    if (APIError.handle(err)) {
      return;
    }

    var error = ErrorUtils.getFormattedError(err);

    if (ErrorCodes.DOMAIN_IN_USE == error.code) {
      error.message = "That name is already in use";
    }

    this.state.views.manage.saving = false;
    this.state.views.manage.error = error;

    this.trigger(this.state);
  },

  _onUserDomainsChange(domainsData) {
    this._copyDomainStoreProps(domainsData);

    this._userDomainsUnsubscribe?.();
    this._userDomainsUnsubscribe = null;

    this.trigger(this.state);
  },

  _copyDomainStoreProps(domainsData) {
    this.state.views.select.domains = domainsData?.domains ?? [];
    this.state.views.select.domainsLoaded = domainsData !== undefined;
    this.state.views.select.domainsLoading = false;
  },

  getInitialState() {
    this.state = _.cloneDeep(model);
    this.state.view = VIEW_SELECT;

    const domainsData = getUserDomainsData();
    if (domainsData) {
      this._copyDomainStoreProps(domainsData);
    } else {
      this._userDomainsUnsubscribe?.();
      this._userDomainsUnsubscribe = subscribeToQuery(
        [USER_DOMAINS],
        this._onUserDomainsChange.bind(this)
      );

      fetchUserDomains().catch((err) => {
        console.error(err);
        // The subscription only fires on cache DATA (subscribeToQuery skips
        // undefined), so a rejected fetch would otherwise leave the modal on
        // its loading spinner forever with the rejection swallowed. Settle
        // the state and notify -- the old UserDomainsStore.onLoadDomainsFail
        // path also settled (error: true, loading: false), though the view
        // never surfaced it; domainsError gives it something to render.
        this.state.views.select.domainsLoading = false;
        this.state.views.select.domainsError = true;
        this.trigger(this.state);
      });
    }

    return this.state;
  },

  getState() {
    return this.state;
  },
});

export default DomainManagementStore;
export { VIEW_MANAGE, VIEW_SELECT };
